import { useContext, useEffect, useRef } from "react";
import { Box, Icon, Typography } from "@mui/material";
import { NavLink } from "react-router-dom";
import { getPageRoute } from "src/services";
import { AuthContext } from "src/utils/AuthContext.js";
import { trackNavItemClicked } from "src/utils/sectionAnalytics.js";
import { useBrowseSeen } from "src/utils/useBrowseSeen.js";

export const BOTTOM_TAB_BAR_HEIGHT = 68;

/**
 * The mobile primary destinations. `Browse` carries no route — it opens the
 * browse sheet — so it's rendered as a button; the rest are `NavLink`s that
 * light up on their active route.
 */
const TABS = [
  {
    key: "home",
    label: "Home",
    icon: "home",
    to: getPageRoute("home", "HOME"),
  },
  { key: "browse", label: "Browse", icon: "grid_view", action: "browse", hasBadge: true },
  {
    key: "search",
    label: "Search",
    icon: "search",
    to: getPageRoute("chat", "SEARCH"),
  },
  {
    key: "library",
    label: "Library",
    icon: "newsstand",
    to: getPageRoute("library", "LIBRARY"),
  },
];

/**
 * Shared inner content for a tab (icon over label). `badge` is `"new"` for the
 * first-run "New" pill, `"dot"` for the black dot it collapses to once seen, or
 * null for no badge.
 */
const TabInner = ({ icon, label, badge }) => (
  <>
    <Box sx={{ position: "relative", display: "flex" }}>
      <Icon baseClassName="material-symbols-outlined" className="tab-icon" fontSize="medium">
        {icon}
      </Icon>
      {badge === "new" ? (
        <Typography
          component="span"
          data-testid="tab-badge-new"
          sx={{
            position: "absolute",
            top: "-6px",
            left: "calc(100% - 2px)",
            px: "5px",
            py: "1px",
            borderRadius: "8px",
            fontSize: "0.55rem",
            fontWeight: 700,
            lineHeight: 1.3,
            letterSpacing: "0.02em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            color: "#fff",
            backgroundColor: "#000",
          }}
        >
          New
        </Typography>
      ) : badge === "dot" ? (
        <Box
          component="span"
          data-testid="tab-badge-dot"
          sx={{
            position: "absolute",
            top: "-2px",
            left: "calc(100% - 1px)",
            width: "7px",
            height: "7px",
            borderRadius: "50%",
            backgroundColor: "#000",
          }}
        />
      ) : null}
    </Box>
    <Typography
      className="tab-label"
      sx={{ fontSize: "0.65rem", fontWeight: 600, lineHeight: 1.15 }}
    >
      {label}
    </Typography>
  </>
);

// The active tab reads via a thin indicator bar dropping from the bar's top
// edge plus a filled icon; inactive tabs dim to secondary text.
const tabSx = {
  position: "relative",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "5px",
  py: 1,
  mx: 0.25,
  color: "text.secondary",
  textDecoration: "none",
  cursor: "pointer",
  border: "none",
  background: "none",
  "& .tab-icon": { fontVariationSettings: "'FILL' 0, 'wght' 300" },
  "&.active": { color: "text.primary" },
  "&.active .tab-icon": { fontVariationSettings: "'FILL' 1, 'wght' 400" },
  "&.active .tab-label": { fontWeight: 700 },
  "&.active::before": {
    content: '""',
    position: "absolute",
    top: "-1px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "40px",
    height: "3px",
    borderRadius: "0 0 3px 3px",
    backgroundColor: "text.primary",
  },
};

/**
 * Persistent bottom navigation for mobile — the always-visible replacement for
 * the buried hamburger, and a bridge to the native app's tab layout. `Browse`
 * opens the browse sheet; the other tabs route.
 *
 * @param {{ onBrowseClick: () => void, browseActive?: boolean }} props
 */
const BottomTabBar = ({ onBrowseClick, browseActive = false }) => {
  const { user } = useContext(AuthContext) ?? {};
  const [browseSeen, markBrowseSeen] = useBrowseSeen();
  const trackTab = (tab) =>
    trackNavItemClicked({ user, surface: "bottom-bar", item: tab.key });
  const badgeFor = (tab) => (tab.hasBadge ? (browseSeen ? "dot" : "new") : null);

  // iOS keeps the layout viewport at its chrome-expanded height while Safari's
  // chrome is collapsed, so a fixed bottom:0 bar floats above the real bottom
  // of the screen. Track the visual viewport and push the bar down by the
  // revealed distance; never negative — a shrunken visual viewport is the
  // keyboard, and the bar belongs behind it, not riding on top.
  const barRef = useRef(null);
  useEffect(() => {
    const viewport = window.visualViewport;
    const bar = barRef.current;
    if (!viewport || !bar) return undefined;

    let frame = 0;
    const sync = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const revealed = viewport.offsetTop + viewport.height - window.innerHeight;
        bar.style.transform = revealed > 0 ? `translateY(${revealed}px)` : "";
      });
    };
    sync();
    viewport.addEventListener("resize", sync);
    viewport.addEventListener("scroll", sync);
    window.addEventListener("scroll", sync, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      viewport.removeEventListener("resize", sync);
      viewport.removeEventListener("scroll", sync);
      window.removeEventListener("scroll", sync);
    };
  }, []);

  return (
    <Box
      component="nav"
      ref={barRef}
      aria-label="Primary"
      data-testid="bottom-tab-bar"
      sx={{
        display: { xs: "flex", md: "none" },
        // Fixed rather than offset from a viewport unit: Safari recomputes
        // `dvh` on its own schedule, so a `dvh` anchor lags its toolbar.
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        // Capped: iOS reports a 49px bottom inset once the page extends under
        // the home indicator, and the full slab beneath the icons reads as the
        // bar floating high. 12px keeps the icons clear of the indicator.
        height: `calc(${BOTTOM_TAB_BAR_HEIGHT}px + min(env(safe-area-inset-bottom), 12px))`,
        pb: "min(env(safe-area-inset-bottom), 12px)",
        backgroundColor: "paper.primary",
        borderTop: (theme) => `1px solid ${theme.palette.border.tertiary}`,
      }}
    >
      {TABS.map((tab) =>
        tab.action === "browse" ? (
          <Box
            key={tab.key}
            component="button"
            type="button"
            onClick={() => {
              trackTab(tab);
              markBrowseSeen();
              onBrowseClick();
            }}
            aria-label="Browse"
            data-testid="bottom-tab-browse"
            className={browseActive ? "active" : ""}
            sx={tabSx}
          >
            <TabInner icon={tab.icon} label={tab.label} badge={badgeFor(tab)} />
          </Box>
        ) : (
          <Box
            key={tab.key}
            component={NavLink}
            to={tab.to}
            end={tab.to === "/"}
            onClick={() => trackTab(tab)}
            data-testid={`bottom-tab-${tab.key}`}
            sx={tabSx}
          >
            <TabInner icon={tab.icon} label={tab.label} badge={badgeFor(tab)} />
          </Box>
        ),
      )}
    </Box>
  );
};

export default BottomTabBar;
